<script setup lang="ts">
import type { FormSubmitEvent, SelectItem } from '@nuxt/ui'
import * as z from 'zod'

import { DEFAULT_CLUSTER, type BrokerType } from '~~/config/clusters'

const props = withDefaults(defineProps<{
  locationName?: string
  // Welk formulier voorgeselecteerd staat. Een aankoopmakelaar-artikel hoort
  // op "Aankoop" te openen, een taxatie-artikel op "Taxatie".
  defaultBrokerType?: BrokerType
}>(), {
  defaultBrokerType: DEFAULT_CLUSTER.cta,
})

const hasLocation = computed(() => !!props.locationName)

const title = computed(() => hasLocation.value
  ? `Vind de beste makelaar in ${props.locationName}`
  : 'Vergelijk alle makelaars in Nederland',
)

const description = computed(() => hasLocation.value
  ? `Vul je postcode in en vergelijk direct de beste makelaars in ${props.locationName}.`
  : 'Vergelijk prestaties en tarieven van makelaars, en vind de beste om je huis te verkopen.',
)

const features = computed(() => hasLocation.value
  ? ['Lokale specialisten', 'Gratis & vrijblijvend', 'Binnen 2 minuten']
  : ['Data-gedreven aanbevelingen', 'Geen verplichtingen', 'Snel & eenvoudig'],
)

const content = {
  postcode: {
    label: 'Jouw postcode',
    placeholder: 'bijv. 1234 AB',
  },
  button: 'Vergelijk makelaars',
}

const buttonUi = {
  base: 'w-full justify-center cursor-pointer',
}

interface LocationResponse {
  street: string
  streetNen: string
  startHouseNumber: string
  endHouseNumber: string
  houseNumberType: string
  city: string
  cityShort: string
  cityId: string
  municipality: string
  municipalityShort: string
  municipalityId: string
  province: string
}

const location = ref<LocationResponse | null>(null)
const loading = ref(false)
const error = ref('')
const { $posthog } = useNuxtApp()

const brokerTypes: SelectItem[] = [
  {
    label: 'Aankoop',
    value: 'vergelijk-aankoop',
  },
  {
    label: 'Verkoop',
    value: 'vergelijk-verkoop',
  },
  {
    label: 'Taxatie',
    value: 'vergelijk-taxatie',
  },
]

const schema = z.object({
  postcode: z.string().min(1, 'Postcode is verplicht'),
  brokerType: z.string(),
})

type Schema = z.output<typeof schema>

const state = reactive<Schema>({
  postcode: '',
  brokerType: props.defaultBrokerType,
})

const isValidPostcode = (postcode: string) => {
  const cleanPostcode = postcode.replace(/\s/g, '')
  return /^\d{4}[a-zA-Z]{2}$/.test(cleanPostcode)
}

const fetchLocation = async (postcode: string) => {
  if (!isValidPostcode(postcode)) {
    location.value = null
    error.value = ''
    return
  }

  loading.value = true
  error.value = ''

  try {
    const cleanPostcode = postcode.replace(/\s/g, '')
    const response = await $fetch<LocationResponse[]>(`https://postcode.bartligthart.workers.dev/?postcode=${cleanPostcode}`)

    if (!response?.length) {
      throw new Error('Geen locatie gevonden voor deze postcode')
    }

    const locationData = response[0]
    if (!locationData) {
      throw new Error('Geen locatie gevonden voor deze postcode')
    }

    location.value = locationData
    error.value = ''
  }
  catch {
    location.value = null
    error.value = 'Geen locatie gevonden voor deze postcode'
  }
  finally {
    loading.value = false
  }
}

watch(() => state.postcode, (newPostcode) => {
  if (newPostcode) {
    fetchLocation(newPostcode)
  }
  else {
    location.value = null
    error.value = ''
  }
})

const onSubmit = (event: FormSubmitEvent<Schema>) => {
  event.preventDefault()
  const brokerType = event.data.brokerType

  $posthog()?.capture('sidebar_form_submit', {
    form_type: 'compare_agents',
    broker_type: brokerType,
    postcode: event.data.postcode,
    city: location.value?.city,
    province: location.value?.province,
    location_name: props.locationName,
  })

  useRouter().push(`/${brokerType}/?postcode=${event.data.postcode}`)
}
</script>

<template>
  <div class="bg-muted rounded-xl p-6 mb-8 border border-default">
    <h2 class="text-xl font-bold tracking-[-0.015em] text-highlighted mb-2">
      {{ title }}
    </h2>

    <p class="text-sm text-muted mb-5 text-pretty">
      {{ description }}
    </p>

    <ul class="space-y-2 mb-6">
      <li
        v-for="feature in features"
        :key="feature"
        class="flex items-center gap-2"
      >
        <UIcon
          name="i-lucide-check-circle"
          class="text-success size-4 shrink-0"
        />
        <span class="text-sm text-muted">{{ feature }}</span>
      </li>
    </ul>

    <UForm
      class="space-y-3"
      :schema="schema"
      :state="state"
      @submit="onSubmit"
    >
      <div class="grid grid-cols-2 gap-2">
        <UFormField
          label="Postcode"
          name="postcode"
        >
          <UInput
            v-model="state.postcode"
            :placeholder="content.postcode.placeholder"
            size="xl"
          />
        </UFormField>

        <UFormField
          label="Makelaar voor:"
          name="brokerType"
        >
          <USelect
            v-model="state.brokerType"
            :items="brokerTypes"
            class="w-full"
            size="xl"
          />
        </UFormField>
      </div>

      <UButton
        variant="solid"
        color="secondary"
        size="xl"
        :ui="buttonUi"
        type="submit"
        trailing-icon="i-brand-arrow-alt"
      >
        {{ content.button }}
      </UButton>

      <div
        v-if="location"
        class="flex items-center gap-2"
      >
        <UIcon
          name="i-lucide-check-circle"
          class="text-success size-4 shrink-0"
        />
        <span class="text-success font-medium text-sm">{{ location?.street }}, {{ location?.city }} gevonden</span>
      </div>
    </UForm>
  </div>
</template>
